import { Client } from '@threatadvisor/react-utils/src/types/generated.Framework';
import React, { ReactElement } from 'react';
import AverageClientReponseTime from './components/AverageClientResponseTime';
import AverageTimeClosureByPriority from './components/AverageTimeClosureByPriority';
import AverageTimeToActionByPriority from './components/AverageTimeToActionByPriority';
import AverageTimeToEscalationByPriority from './components/AverageTimeToEscalationByPriority';
import DetectionCaseByClosureReason from './components/DetectionCaseByClosureReason';
import DetectionCaseStatisticsTable from './components/DetectionCaseStatisticsTable';
import DetectionCaseVolumeByDay from './components/DetectionCaseVolumeByDay';
import DetectionCasesByAlertType from './components/DetectionCasesByAlertType';
import DetectionCasesByPriority from './components/DetectionCasesByPriority';
import EventClosureStatus from './components/EventClosureStatus';
import EventClosureStatusByWeek from './components/EventClosureStatusByWeek';
import EventStatistics from './components/EventStatistics';
import EventVolumeByDay from './components/EventVolumeByDay';
import EventVolumeByWeek from './components/EventVolumeByWeek';
import EventsByAlertType from './components/EventsByAlertType';
import EventsByTactic from './components/EventsByTactic';
import EventsByTechnique from './components/EventsByTechnique';
import MdrCases from './components/MdrCases';
import MdrTeam from './components/MdrTeam';
import Percentages from './components/Percentages';
import TableOfContents, { ReportSection } from './components/TableOfContents';

type DetectionResponseProps = {
  clientUid: string;
  reportStart: string;
  reportEnd: string;
  securityEventApi: string;
  clientSettings?: Client;
};

export default function DetectionResponse(props: DetectionResponseProps): ReactElement {
  const { clientUid, reportStart, reportEnd, securityEventApi, clientSettings } = props;

  const reportSections: ReportSection[] = [
    {
      title: `${clientSettings?.name} MDR Program Support Team`,
      id: 'managed-detection-teampage',
      className: 'section report-section page-break',
      indentCount: 0,
      component: (
        <MdrTeam
          securityEventApi={securityEventApi}
          clientUid={clientUid}
          clientName={clientSettings?.name ?? ''}
        />
      )
    },
    {
      title: 'Events',
      id: 'managed-detection-events-header',
      indentCount: 0,
      headerOnly: true
    },
    {
      title: 'Event Statistics',
      id: 'managed-detection-event-statistics',
      className: 'section report-section page-break',
      indentCount: 1,
      component: (
        <EventStatistics clientUid={clientUid} reportStart={reportStart} reportEnd={reportEnd} />
      )
    },
    {
      title: 'Top Event Types',
      id: 'managed-detection-events-by-alert-type',
      className: 'section report-section page-break',
      indentCount: 1,
      component: (
        <EventsByAlertType
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
          securityEventApi={securityEventApi}
        />
      )
    },
    {
      title: 'Events by Tactic',
      id: 'managed-detection-events-by-tactic',
      className: 'report-section',
      indentCount: 1,
      component: (
        <EventsByTactic
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
          securityEventApi={securityEventApi}
        />
      )
    },
    {
      title: 'Events by Technique',
      id: 'managed-detection-events-by-technique',
      className: 'report-section',
      indentCount: 1,
      component: (
        <EventsByTechnique
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
          securityEventApi={securityEventApi}
        />
      )
    },
    {
      title: 'Event Closure Status',
      id: 'managed-detection-event-closure-status',
      className: 'report-section',
      indentCount: 1,
      component: (
        <EventClosureStatus
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
          securityEventApi={securityEventApi}
        />
      )
    },
    {
      title: 'Event Volume By Day',
      id: 'managed-detection-event-vol-by-day',
      className: 'report-section',
      indentCount: 1,
      component: (
        <EventVolumeByDay clientUid={clientUid} reportStart={reportStart} reportEnd={reportEnd} />
      )
    },
    {
      title: 'Event Volume By Source',
      id: 'managed-detection-event-vol-by-week',
      className: 'report-section',
      indentCount: 1,
      component: (
        <EventVolumeByWeek clientUid={clientUid} reportStart={reportStart} reportEnd={reportEnd} />
      )
    },
    {
      title: 'Event Closure Status By Week',
      id: 'managed-detection-event-closure-by-week',
      className: 'report-section',
      indentCount: 1,
      component: (
        <EventClosureStatusByWeek
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      )
    },
    {
      title: 'Detection Cases',
      id: 'managed-detection-detection-cases-header',
      indentCount: 0,
      headerOnly: true
    },
    {
      title: 'Detection Case Statistics',
      id: 'managed-detection-detection-case-statistics',
      className: 'report-section page-break',
      indentCount: 1,
      component: (
        <DetectionCaseStatisticsTable
          securityEventApi={securityEventApi}
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      )
    },
    {
      title: 'Detection Cases By Alert Type',
      id: 'managed-detection-cases-by-alert-type',
      className: 'report-section',
      indentCount: 1,
      component: (
        <DetectionCasesByAlertType
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      )
    },
    {
      title: 'Detection Case Volume By Day',
      id: 'managed-detection-case-volume-by-day',
      className: 'report-section',
      indentCount: 1,
      component: (
        <DetectionCaseVolumeByDay
          securityEventApi={securityEventApi}
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      )
    },
    {
      title: 'Detection Cases By Priority',
      id: 'managed-detection-cases-by-priority',
      className: 'report-section',
      indentCount: 1,
      component: (
        <DetectionCasesByPriority
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      )
    },
    {
      title: 'Detection Case By Closure Reason',
      id: 'managed-detection-case-by-close-reason',
      className: 'report-section',
      indentCount: 1,
      component: (
        <DetectionCaseByClosureReason
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      )
    },
    {
      title: 'Averages',
      id: 'managed-detection-averages-header',
      indentCount: 0,
      headerOnly: true
    },
    {
      title: 'Average Time to Action by Priority Level',
      id: 'managed-avg-time-to-action',
      className: 'report-section',
      indentCount: 1,
      component: (
        <AverageTimeToActionByPriority
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
          securityEventApi={securityEventApi}
        />
      )
    },
    {
      title: 'Average Time to Escalation by Priority Level',
      id: 'managed-avg-time-to-escalation',
      className: 'report-section',
      indentCount: 1,
      component: (
        <AverageTimeToEscalationByPriority
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
          securityEventApi={securityEventApi}
        />
      )
    },
    {
      title: 'Average Client Response Time',
      id: 'managed-avg-client-response-time',
      className: 'report-section',
      indentCount: 1,
      component: (
        <AverageClientReponseTime
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
          securityEventApi={securityEventApi}
        />
      )
    },
    {
      title: 'Average Time to Closure by Priority Level',
      id: 'managed-avg-time-to-closure',
      className: 'report-section',
      indentCount: 1,
      component: (
        <AverageTimeClosureByPriority
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
          securityEventApi={securityEventApi}
        />
      )
    },
    {
      title: 'Percentages',
      id: 'managed-detection-percentages',
      className: 'report-section page-break',
      indentCount: 0,
      component: (
        <Percentages
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
          securityEventApi={securityEventApi}
        />
      )
    },
    {
      title: 'Cases for Review',
      id: 'mdr-cases',
      className: 'report-section page-break',
      indentCount: 0,
      component: <MdrCases {...props} />
    }
  ];

  return (
    <>
      <TableOfContents reportSections={reportSections} />

      {reportSections.map(section => {
        if (!section.headerOnly) {
          return (
            <div className={section.className} id={`${section.id}`} key={`k-${section.id}`}>
              {section.component}
            </div>
          );
        }
      })}

      <div className="report-section page-break" id="managed-detection-mitre-atk">
        <h2>Appendix A</h2>
        <h3>MITRE ATT&CK Framework</h3>
        <p>
          The MITRE ATT&CK Framework is a way of understanding how attackers move through a system.
          It's like a playbook for attackers, and it can be used by defenders to predict and stop
          attacks.
        </p>
        <p>
          The framework is divided into two parts: tactics and techniques. Tactics are the
          high-level goals that attackers want to achieve, like gaining access to a system or
          stealing data. Techniques are the specific steps that attackers take to achieve their
          tactics.
        </p>
        <p>
          For a more in-depth look at the Framework, visit their website at{' '}
          <a href="https://attack.mitre.org" target="_blank" rel="noreferrer noopener">
            https://attack.mitre.org
          </a>
          .
        </p>
        <h3>Event/Case Closure Statuses</h3>

        <ul>
          <li>
            <strong>Known Issue: </strong>Known issues are events generated by known system
            processes which, by design, behave in a suspicious manner and which cannot be tuned
            further, but are allowed by the client. Examples include: Vulnerability Scanners, Avaya
            VPNs, expired Kerberos tickets from Outlook, etc.
          </li>
          <li>
            <strong>Accepted Behavior: </strong>User behavior which, while suspicious, the client
            has indicated is permissible, expected, or cannot be stopped. Examples include VPN
            Ingress from foreign country where user is known to be travelling to, permitted user
            creation activity by admins, other behavior which has been documented to be permitted.
          </li>
          <li>
            <strong>Tuning Required: </strong>System behavior generating alerts which are erroneous
            or otherwise valueless, and which requires additional tuning or reconfiguration to
            reduce noise. Examples: Improperly deployed networks sensors, improperly correlated
            rules, erroneous threat intelligence feeds.
          </li>
          <li>
            <strong>No Action Needed: </strong>Customer-submitted events, SPAM, test events, and
            garbled messages which require no action, or for which work has already been completed,
            and which do not require a response, or a response has already been sent by other means.
          </li>
          <li>
            <strong>Resolved: </strong>Incident has been resolved and closed. Any work instructions
            and / or tuning has been completed.
          </li>
          <li>
            <strong>No Response: </strong>Incident was escalated to the client, and DirectDefense
            did not receive a response.
          </li>
          <li>
            <strong>Linked: </strong>Event was added to an existing case.
          </li>
        </ul>
      </div>
    </>
  );
}
